import {Component, OnInit, Inject, PLATFORM_ID, ViewEncapsulation} from '@angular/core';
import {isPlatformBrowser, Location} from '@angular/common';
import Splide from '@splidejs/splide';
import {BlogService} from 'src/app/core/services/api-service/blog.service';
import {ResponseHandlerService} from 'src/app/core/services/common-service/response-handler.service';
import {lastValueFrom} from 'rxjs';
import {CommonConstants, RoutesConstants, ImagePathConstants, ImageSizeConstants} from '@coreconstant';
import {RequestParamModel} from 'src/app/core/models/request-param-model';
import {ActivatedRoute, Router, RouterModule} from '@angular/router';
import {SetImageDimensionsDirective} from 'src/app/core/directives/set-image-dimensions.directive';
import {FormsModule} from '@angular/forms';
import {SeoService} from '../../core/services/common-service/seo.service';
import {MetaDataConstants} from '../../core/constants/meta-data-constants';
import {BlogCategoryService} from '../../core/services/api-service/blog-category.service';

@Component({
    selector: 'app-wedspiration-list',
    imports: [SetImageDimensionsDirective, RouterModule, FormsModule],
    templateUrl: './wedspiration-list.component.html',
    styleUrl: './wedspiration-list.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class WedspirationListComponent implements OnInit {
    requestParamModel!: RequestParamModel;
    routesConstants = RoutesConstants
    imagePathConstants = ImagePathConstants
    imageSizeConstants = ImageSizeConstants
    commonConstants = CommonConstants
    featureBlogList = [];
    trendingBlogs = [];
    blogList = [];
    blogCategories = [];
    selectedBlogCategoriesName: string = ''
    currentPage = 1
    pageCount;
    totalCount = 0;
    firstSegment: string;
    slug: string
    searchValue: string;
    featuredArticle = null
    searchTag: string;
    isShowClear: boolean = false
    isFilterApply = false
    isGetResponse: boolean = false

    showCloseSearchIcon: boolean = false;

    constructor(
        @Inject(PLATFORM_ID) private platformId: object,
        private _blogService: BlogService,
        private _responseHandlerService: ResponseHandlerService,
        private _blogCategoryService: BlogCategoryService,
        private location: Location,
        private _seoService: SeoService,
        private _router: Router,
        private _activatedRoute: ActivatedRoute
    ) {
        this.setMetaTags()
        this.requestParamModel = new RequestParamModel();
        this.requestParamModel.pageSize = CommonConstants.BLOG_PAGE_SIZE_COUNT
        this.requestParamModel.pageIndex = 1
        this.firstSegment = this.location.path();

        this._activatedRoute.queryParams.subscribe(params => {
            this.firstSegment = this.location.path().split('?')[0];
            this.searchTag = params['tag']

            this.requestParamModel.removeFilter('blogTag')
            if (this.searchTag) {
                this.requestParamModel.setFilter('blogTag', this.searchTag)
            }
        });
    }

    /**
     * Scrolls smoothly to the specified section of the page based on the section ID and adjusts for header offset.
     */
    scrollToSection() {
        if (isPlatformBrowser(this.platformId)) {
            window.scrollTo({top: 0, behavior: 'smooth'});
        }
    }

    /**
     * Sets the meta tags for the current page by creating a meta tag object and passes it to the SEO service for application.
     * @return {void} Does not return a value.
     */
    setMetaTags() {
        let metaTagObject = {
            title: MetaDataConstants.WEDSPIRATION.TITLE,
            description: MetaDataConstants.WEDSPIRATION.DESCRIPTION,
            ogImageUrl: MetaDataConstants.WEDSPIRATION.OGIMAGEURL,
            ogWebUrl: this.routesConstants.WEDSPIRATION + (this.slug ? '/' + this.slug : ''),
        }
        this._seoService.setMetaTags(metaTagObject)
    }

    /**
     * Initializes the component and its dependent processes.
     */
    ngOnInit(): void {
        const params = this._activatedRoute.snapshot.params;
        if (params && params['type']) {
            this.slug = params['type']
            this.setBlogsFilter()
        }
        this.getFeaturedBlog()
        this.getTrendingBlogs()
        this.getBlogsList(false, true)
        this.getFeatureBlogsList()
        this.getBlogsCategories()
        this.setMetaTags()
    }

    /**
     * Retrieves the featured blog article by making a request to the blog service.
     */
    getFeaturedBlog() {
        let requestModel = new RequestParamModel();
        requestModel.setFilter("isFeatured", 1);
        requestModel.pageSize = 1;
        let getFeaturedBlog = this._blogService.getBlogsList(requestModel)
        lastValueFrom(getFeaturedBlog).then((res: any) => {
            if (res["data"] && res["data"].length > 0) {
                this.featuredArticle = res["data"][0];
            }
        }, (error: any) => {
            this._responseHandlerService.handleAPIErrorResponse(error)
        });
    }

    /**
     * Fetches and sets the trending blogs based on a predefined filter.
     */
    getTrendingBlogs() {
        let requestModel = new RequestParamModel();
        requestModel.setFilter("isTrending", 1);
        requestModel.pageSize = 4;
        let getListing = this._blogService.getBlogsList(requestModel)
        lastValueFrom(getListing).then((res: any) => {
            if (res["data"]) {
                this.trendingBlogs = res["data"];
            } else {
                this._responseHandlerService.showErrorMessageGeneral(res["message"] ?? this.commonConstants.SOMETHING_WENT_WRONG)
            }
        }, (error: any) => {
            this._responseHandlerService.handleAPIErrorResponse(error)
        });
    }

    /**
     * Retrieves the list of blogs with filters.
     * @param {boolean} [isLoadMore=false] - Indicates if additional blogs should be loaded and appended to the existing list.
     * @param {boolean} [isPageLoad=false] - Indicates if the method is called as part of an initial page load.
     * @param {boolean} [isSearchActioned=false] - Indicates if the method is invoked due to a search action.
     */
    getBlogsList(isLoadMore: boolean = false, isPageLoad: boolean = false, isSearchActioned: boolean = false) {
        this.isFilterApply = false;

        let getBlogList = this._blogService.getBlogsList(this.requestParamModel);
        lastValueFrom(getBlogList).then((res: any) => {
            this.currentPage = res["currentPage"];
            this.pageCount = res["pageCount"];
            this.totalCount = res["totalCount"];
            if (res["data"]) {
                this.isGetResponse = true;
                let data = res["data"];

                if (isLoadMore)
                    this.blogList = this.blogList.concat(data);
                else
                    this.blogList = data;
            }

            if (this.firstSegment == '/' + this.routesConstants.WEDSPIRATION)
                setTimeout(() => this.sliderReInit(), 100);

            if (this.currentPage == 1) {
                if (isSearchActioned && this.blogList.length > 0) {
                    const element = document.getElementById('blog-listing-wrap');
                    if (element) {
                        const elementPosition = element.getBoundingClientRect().top + window.scrollY;

                        // Adjust offset based on screen width
                        const offset = window.innerWidth <= 768 ? 250 : 100;

                        window.scrollTo({
                            top: elementPosition - offset,
                            behavior: 'smooth'
                        });
                    }
                } else {
                    this.scrollToSection();
                }
            }

        }, (error: any) => {
            this._responseHandlerService.handleAPIErrorResponse(error);
        });
    }


    /**
     * Fetches and processes a list of featured blogs from the blog service.
     */
    getFeatureBlogsList() {
        let requestModel = new RequestParamModel();
        requestModel.addDefaultFilter("type", "random")
        requestModel.pageSize = 8;
        let getFeatureBlogList = this._blogService.getBlogsList(requestModel)
        lastValueFrom(getFeatureBlogList).then((res: any) => {
            if (res["data"]) {
                this.featureBlogList = res["data"];
                if (this.featureBlogList.length > 0) {
                    setTimeout(() => this.sliderReInit(), 500);
                }
            } else {
                this._responseHandlerService.showErrorMessageGeneral(res["message"] ?? this.commonConstants.SOMETHING_WENT_WRONG)
            }
        }, (error: any) => {
            this._responseHandlerService.handleAPIErrorResponse(error)
        });
    }

    /**
     * Reinitializes and configures the slider for the parent element using the Splide library.
     */
    sliderReInit() {
        if (isPlatformBrowser(this.platformId)) {

            // Initialize the parent slider
            const parentSlider = new Splide('#parent-slider', {
                type: 'slide',
                perPage: 4,
                pagination: false,
                arrows: true,
                drag: false,
                gap: '5px',
                perMove: 1,
                breakpoints: {
                    480: {
                        type: 'loop',
                        perPage: 2,
                        perMove: 1,
                        autoWidth: true,
                    },
                    767: {
                        type: 'loop',
                        perPage: 2,
                        perMove: 1,
                        autoWidth: true,
                    },
                    991: {
                        type: 'loop',
                        perPage: 3,
                        perMove: 1,
                        autoWidth: true,
                    },
                    1024: {
                        type: 'loop',
                        perPage: 4,
                        perMove: 1,
                        autoWidth: true,
                    }
                },
            });

            // Mount the parent slider
            parentSlider.mount();
        }
    }

    /**
     * Increments the page index for blog retrieval and fetches the next set of blogs.
     */
    loadMoreBlogs() {
        this.requestParamModel.pageIndex = this.requestParamModel.pageIndex + 1;
        this.getBlogsList(true);
    }

    /**
     * Fetches and processes the list of blog categories.
     */
    getBlogsCategories() {
        const requestModel = new RequestParamModel();
        requestModel.addDefaultFilter('data', "all")
        delete requestModel.pageSize
        let getBlogCategories = this._blogCategoryService.getBlogCategories(requestModel)
        lastValueFrom(getBlogCategories).then((res: any) => {
            if (res["data"]) {
                this.blogCategories = res["data"];
                if (this.slug) {
                    this.selectedBlogCategoriesName = res["data"]?.find((category) => category?.slug == this.slug)?.name
                }
            } else {
                this._responseHandlerService.showErrorMessageGeneral(res["message"] ?? this.commonConstants.SOMETHING_WENT_WRONG)
            }
        }, (error: any) => {
            this._responseHandlerService.handleAPIErrorResponse(error)
        });
    }

    /**
     * Updates the style and behavior of blog categories based on the provided slug and name.
     * @param {any} slug - The unique identifier or path for the blog category to navigate to.
     * @param {string} [name] - The name of the selected blog category.
     */
    blogCategoriesStyleChange(slug: any, name?: string) {
        this.slug = slug
        this.selectedBlogCategoriesName = name == undefined ? '' : name
        this.requestParamModel.pageIndex = 1
        const url = slug ?
            `${this.routesConstants.WEDSPIRATION}/${slug}` :
            this.routesConstants.WEDSPIRATION;
        this.location.go(url)
        this.firstSegment = this.location.path();
        this.setBlogsFilter()
        this.getBlogsList(false, false, true)
        this.setMetaTags()
    }

    /**
     * Sets the blog filter based on the current slug by first removing any
     * */
    setBlogsFilter() {
        this.requestParamModel.removeFilter('blogCategorySlug')
        this.requestParamModel.setFilter('blogCategorySlug', this.slug)

        let findCategories = this.blogCategories.find((category) => category.slug == this.slug)
        if(findCategories == null){
                this._router.navigate(['/' + this.routesConstants.PAGE_NOT_FOUND], {skipLocationChange: true});
                return
        }
    }

    /**
     * Clears all search-related data, including resetting the search input value and hiding the close search icon.
     */
    clearAll() {
        this.showCloseSearchIcon = false;
        this.searchValue = ''
        this.searchBlogs()
    }

    /**
     * Searches blogs based on the current filter and search parameters.
     */
    searchBlogs() {
        if (this.slug) {
            this.requestParamModel.removeFilter('blogCategorySlug')
            this.requestParamModel.setFilter('blogCategorySlug', this.slug)
        }
        this.isShowClear = false
        this.requestParamModel.removeFilter('search')
        this.requestParamModel.setFilter('search', this.searchValue)

        if (this.requestParamModel && this.requestParamModel.filter != '') {
            this.isFilterApply = true
        }

        if (this.searchValue) {
            this.isShowClear = true
        }
        this.getBlogsList(false, false, true);
    }

    /**
     * Sets the default image for an HTMLImageElement when an error occurs
     * @param {Event} event - The event object associated with the image load error.
     */
    setDefaultImage(event: Event) {
        let image = this.commonConstants.DEFAULT_NO_IMAGE;
        (event.target as HTMLImageElement).src = image
    }

    /**
     * Handles the search keyword input change event and updates internal state.
     * @param {Object} event The event object triggered by the input change. The `event.target.value` contains the user's input.
     */
    searchKeywords(event) {
        const value = event.target.value;
        this.showCloseSearchIcon = true
        if (value.length == 0) {
            this.showCloseSearchIcon = false
        }
    }
}
